import { Flex, Text } from "@minervaproject/fds";
import { useState } from "react";

import {
  BreakoutResourceDetails,
  InstructorBreakoutResources,
  StudentBreakoutResources,
  VisitingBreakoutResources,
} from "./breakout_resources";
import { NewResources } from "./new_resources";
import { ResourceButton } from "./resource_button";

import { FlightErrorBoundary } from "components/error_boundary";
import { useCapability, useUserIdsWithCapability } from "hooks/capabilities";
import { useAppSelector } from "hooks/redux_hooks";
import { BreakoutType, CapabilityType } from "shared/types";
import { classroomSelectors } from "store";
import { isClassroomResource, isGeneratedResource, isVisibleToStudents } from "utils/resource";

function Resources() {
  const [selectedBreakout, setSelectedBreakout] = useState<BreakoutType | null>(null);
  const canControlClassroom = useCapability(CapabilityType.CAN_CONTROL_CLASSROOM);
  const canParticipateInClassUserIds = useUserIdsWithCapability(
    CapabilityType.CAN_PARTICIPATE_IN_CLASS,
  );
  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const preclassResourceId = useAppSelector(classroomSelectors.selectPreclassResourceId());
  const resources = Object.values(useAppSelector(classroomSelectors.selectResources()));
  const preclassResource = resources.filter((resource) => resource.id === preclassResourceId)[0];

  const filteredResources = resources.filter(
    (resource) =>
      isClassroomResource(resource) &&
      !resource.is_breakout_group_notes &&
      resource.id !== preclassResourceId,
  );

  // Added Resources are resources that were added in class.
  // A resource was added in class if there is an uploader id.
  const addedResources = filteredResources.filter((resource) => {
    if (!resource.uploader_id) return false;

    if (
      // Staff can always see all resources.
      canControlClassroom ||
      // Forum docs and whiteboards, a.k.a. generated resources, are always visible to everyone.
      isGeneratedResource(resource) ||
      // Resources uploaded by students are always visible to everyone.
      canParticipateInClassUserIds.includes(resource.uploader_id)
    )
      return true;

    // Resources uploaded by staff are only visible to students if they are marked as visible.
    return isVisibleToStudents(resource);
  });

  // Lesson Plan Resources are resources that were added through Course Builder and thus
  // do not have an uploader id.
  const lessonPlanResources = filteredResources.filter((resource) => {
    if (resource.uploader_id) return false;

    // Staff can always see all resources.
    if (canControlClassroom) return true;

    // Students can only see lesson plan resources if they are marked as visible.
    return isVisibleToStudents(resource);
  });

  return (
    <Flex className="sidebar-width">
      <Flex auto className="vertical-scroll" direction="column" p={2}>
        {selectedBreakout ? (
          <BreakoutResourceDetails
            breakout={selectedBreakout}
            hideCallback={() => setSelectedBreakout(null)}
          />
        ) : (
          <>
            <NewResources
              canControlClassroom={canControlClassroom}
              activeBreakoutSessionId={activeBreakoutSessionId}
            />

            {canControlClassroom && !!preclassResourceId && (
              <div className="mb5">
                <Text className="body-l flex-none" kind="h2" mb={4}>
                  Pre-Class Work
                </Text>
                <FlightErrorBoundary text="Resource cannot be displayed" margin="0 8px 8px">
                  <ResourceButton resource={preclassResource} />
                </FlightErrorBoundary>
              </div>
            )}

            {canControlClassroom && !activeBreakoutSessionId && (
              <FlightErrorBoundary
                text="Breakout resources cannot be displayed"
                margin="0 8px 16px"
              >
                <InstructorBreakoutResources handleSelectBreakout={setSelectedBreakout} />
              </FlightErrorBoundary>
            )}

            {canControlClassroom && activeBreakoutSessionId && <VisitingBreakoutResources />}

            {!canControlClassroom && <StudentBreakoutResources />}

            {addedResources.length > 0 && (
              <Flex direction="column" none className="mb5">
                <Text className="body-l flex-none" kind="h2" mb={4}>
                  Added Resources
                </Text>
                {addedResources.map((resource) => (
                  <FlightErrorBoundary
                    key={resource.id}
                    text="Resource cannot be displayed"
                    margin="0 8px 8px"
                  >
                    <ResourceButton resource={resource} />
                  </FlightErrorBoundary>
                ))}
              </Flex>
            )}

            {lessonPlanResources.length > 0 && (
              <Flex direction="column" none className="mb5">
                <Text className="body-l flex-none" kind="h2" mb={4}>
                  Lesson Plan Resources
                </Text>
                {lessonPlanResources.map((resource) => (
                  <FlightErrorBoundary
                    key={resource.id}
                    text="Resource cannot be displayed"
                    margin="0 8px 8px"
                  >
                    <ResourceButton resource={resource} />
                  </FlightErrorBoundary>
                ))}
              </Flex>
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
}

export { Resources };
